import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { requestWithUserAuth } from '../../../api/auth'
import { fetchNextTableNumber, updateRegistration } from '../../../api/snacks'
import { useThemeMode } from '../../../theme'

const makeStyles = (palette) => ({
  dialogTitle: { color: 'success.main' },
  contentBox: { display: 'flex', flexDirection: 'column', gap: 2, pt: 1 },
  nickLabel: { color: palette.headerColor },
  spinner: { color: 'success.main', mx: 1 }
})

export const ApprovePendingDialog = ({ registration, onClose, onApproved }) => {
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)

  const [tableNumber, setTableNumber] = useState(
    registration?.table_number != null ? String(registration.table_number) : ''
  )
  const [tableError, setTableError] = useState('')
  const [serverError, setServerError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const styles = makeStyles(palette)

  useEffect(() => {
    if (registration?.table_number == null) {
      fetchNextTableNumber(config, auth, (n) => {
        setTableNumber((prev) => (prev === '' ? String(n) : prev))
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const validateTableNumber = (value) => {
    if (!value) return t('snacks-table-number-required')
    const n = parseInt(value, 10)
    if (isNaN(n) || n < 1 || !Number.isInteger(n)) return t('snacks-table-number-invalid')
    return ''
  }

  const handleApprove = () => {
    const err = validateTableNumber(tableNumber)
    setTableError(err)
    if (err) return

    setIsLoading(true)
    const DETAIL_KEYS = {
      'User already has another active registration': 'snacks-error-already-active',
      'table_number already assigned to another registration': 'snacks-table-number-conflict'
    }

    requestWithUserAuth(config, auth, (updatedAuth) => {
      updateRegistration(
        config, updatedAuth, registration.id,
        { status: 'active', table_number: parseInt(tableNumber, 10) },
        () => { setIsLoading(false); if (onApproved) onApproved() },
        (error) => {
          const detail = error?.response?.data?.detail
          const key = detail ? DETAIL_KEYS[detail] : null
          setServerError(key ? t(key) : (detail || t('snacks-error-generic')))
          setIsLoading(false)
        }
      )
    })
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        {t('snacks-approve-title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={styles.contentBox}>
          <DialogContentText>
            {t('snacks-approve-hint')}
          </DialogContentText>
          <Typography variant="subtitle2" sx={styles.nickLabel}>
            {registration?.owner_nick}
          </Typography>
          <TextField
            label={t('snacks-field-table-number')}
            value={tableNumber}
            onChange={(e) => {
              const val = e.target.value
              if (val === '' || /^\d+$/.test(val)) {
                setTableNumber(val)
                setTableError(validateTableNumber(val))
              }
            }}
            error={Boolean(tableError)}
            helperText={tableError || ' '}
            required
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            autoFocus
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>{t('cancel')}</Button>
        {isLoading
          ? <CircularProgress size={28} sx={styles.spinner} />
          : (
              <Button onClick={handleApprove} variant="contained" color="success">
                {t('snacks-approve')}
              </Button>
            )}
      </DialogActions>
    </Dialog>
  )
}
